import { Children, cloneElement, type CSSProperties, isValidElement, useMemo, useRef } from 'react';
import cn from 'clsx';
import { cva } from 'class-variance-authority';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';

import { ArrowThinRight } from '@/icons/ArrowThinRight';
import { ArrowThinLeft } from '@/icons/ArrowThinLeft';
import { useDetectUserTouch } from '@/hooks/useDetectUserTouch';
import { useCustomSlider } from '@/hooks/useCustomSlider';
import { type Breakpoint, breakpoints, useCurrentViewport } from '@/hooks/useCurrentViewport';
import { fillMissingKeys } from '@/components/property-expose/Slider/fillMissingKeys';

import type { SlideProps } from './Slide';

import styles from './Slider.module.css';

export type SlidesPerViewport = {
  [key in Breakpoint]: number;
};

type SliderControlLabels = {
  previous: string;
  next: string;
};

interface SliderProps {
  className?: string;
  children: React.ReactElement<SlideProps> | React.ReactElement<SlideProps>[];
  slidesPerView: Partial<SlidesPerViewport>;
  scrollSlides?: Partial<SlidesPerViewport>;
  controlLabels: SliderControlLabels;
}

interface ScrollToSlideProps {
  scrollSlides: number;
  slidesElement: HTMLUListElement | null;
  slideElement: HTMLLIElement | null;
  behavior?: ScrollBehavior;
}

const arrowButtonVariants = cva(styles.arrowButton, {
  variants: {
    direction: {
      left: styles.arrowButtonLeft,
      right: styles.arrowButtonRight,
    },
  },
});

const scrollToSlide = ({ scrollSlides, slidesElement, slideElement, behavior = 'smooth' }: ScrollToSlideProps) => {
  const currentScollPosition = slidesElement?.scrollLeft ?? 0;
  const itemWidth = slideElement?.clientWidth ?? 0;

  slidesElement?.scroll({
    left: currentScollPosition + itemWidth * scrollSlides,
    behavior: behavior,
  });
};

export const Slider = ({ className, children, slidesPerView, scrollSlides, controlLabels }: SliderProps) => {
  const slidesRef = useRef<HTMLUListElement>(null);
  const slideRef = useRef<HTMLLIElement>(null);
  const slidesElement = slidesRef.current;
  const slideElement = slideRef.current;

  const currentViewport = useCurrentViewport();
  const { previousButtonDisabled, nextButtonDisabled, currentSliderIndex } = useCustomSlider({
    slidesElement,
    slideElement,
  });
  const isTouch = useDetectUserTouch();

  const filledSlidesPerView = useMemo(() => fillMissingKeys<Breakpoint>([...breakpoints], slidesPerView), [slidesPerView]);

  const filledSlidesPerScroll = useMemo(
    () => (scrollSlides ? fillMissingKeys<Breakpoint>([...breakpoints], scrollSlides) : null),
    [scrollSlides],
  );

  const [firstChild, ...restChildren] = Children.toArray(children);
  const totalSlides = Children.count(children);

  const currentScrollSlides = filledSlidesPerScroll ? filledSlidesPerScroll[currentViewport] : filledSlidesPerView[currentViewport];
  // Reason: fillMissingKeys can yield 0 for missing breakpoints; guard so pagination math never divides by zero.
  const safeScrollSlides = Math.max(currentScrollSlides, 1);

  const paginationItemLength = Math.ceil((totalSlides - filledSlidesPerView[currentViewport]) / safeScrollSlides) + 1;

  const activePaginationIndex = Math.round(currentSliderIndex / safeScrollSlides);

  const slidesStyle = {
    '--slides-mobile': Math.min(filledSlidesPerView.mobile, totalSlides),
    '--slides-tablet-portrait': filledSlidesPerView.tabletPortrait,
    '--slides-tablet-landscape': filledSlidesPerView.tabletLandscape,
    '--slides-laptop': filledSlidesPerView.laptop,
    '--slides-desktop': filledSlidesPerView.desktop,
  } as CSSProperties;

  return (
    <div className={cn(styles.slider, className)}>
      <ul className={styles.slides} ref={slidesRef} style={slidesStyle} data-few-slides={totalSlides < 3}>
        {isValidElement(firstChild) &&
          cloneElement(firstChild as React.ReactElement, {
            ref: slideRef,
          })}
        {restChildren}
      </ul>
      {!isTouch && (
        <>
          <IconButton
            className={arrowButtonVariants({ direction: 'left' })}
            label={controlLabels.previous}
            icon={<ArrowThinLeft />}
            size="medium"
            onClick={() =>
              scrollToSlide({
                scrollSlides: currentScrollSlides * -1,
                slidesElement,
                slideElement,
              })
            }
            disabled={previousButtonDisabled}
            variant="primaryAlt"
          />
          <IconButton
            className={arrowButtonVariants({ direction: 'right' })}
            label={controlLabels.next}
            size="medium"
            icon={<ArrowThinRight />}
            onClick={() =>
              scrollToSlide({
                scrollSlides: currentScrollSlides,
                slidesElement,
                slideElement,
              })
            }
            disabled={nextButtonDisabled}
            variant="primaryAlt"
          />
        </>
      )}
      {paginationItemLength > 1 && (
        <div className={styles.pagination}>
          {Array.from({ length: paginationItemLength }).map((_, index) => (
            <div key={index} className={styles.paginationItem} data-active={index === activePaginationIndex} />
          ))}
        </div>
      )}
    </div>
  );
};
